import { AddOutlined } from "@mui/icons-material";
import {
	Autocomplete,
	Box,
	Button,
	Checkbox,
	Dialog,
	DialogContent,
	DialogTitle,
	FormControl,
	Stack,
	TextField,
	Typography,
} from "@mui/material";
import Chip from "@mui/material/Chip";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { ESRThunkDispatch } from "../../../actions/form";
import { updateThread } from "../../../actions/util";
import getBackendURL from "../../../api/api";
import { useCurrentUser } from "../../../hooks/useCurrentUser";
import { request } from "../../../request";
import { ESRState, Thread } from "../../../types/data";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";

type Props = {
	thread: Thread;
};

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

export function AddRecipients({ thread }: Props) {
	const { esrId = "", activeId = "" } = useParams();
	const [user, setUser] = useCurrentUser();

	const dispatch = useDispatch<ESRThunkDispatch>();

	const [editing, setEditing] = useState(false);

	type RecipientOption = [string, string, string, string, string[], boolean];
	const [recipientOptions, setRecipientOptions] = useState<RecipientOption[]>(
		[],
	);
	const [recipients, setRecipients] = useState<string[]>([]);
	// const [groupOptions, setGroupOptions] = useState<string[]>([]);
	// const [groups, setGroups] = useState<string[]>([]);
	const [allowed_user_groups, setAllowedGroups] = useState<string[]>([]);
	const [selected_user_groups, setSelectedUserGroups] = useState<string[]>([]);
	const filteredRecipientOptions = useMemo(() => {
		if (selected_user_groups.length === 0) return [];

		const expanded: [string, string, string, string][] = [];

		recipientOptions.forEach(([email, first, last, organization, userGroups, isSponsor]) => {
			userGroups.forEach((group) => {
				if (selected_user_groups.includes(group)) {
					const exists = expanded.some(([g, e]) => g === group && e === email);
					if (!exists) expanded.push([group, email, first, last]);
				}
			});
		});
		return expanded;
	}, [selected_user_groups, recipientOptions]);

	useEffect(() => {
			if (activeId) {
				request<{
					users: Record<
						string,
						{
							first_name: string;
							last_name: string;
							organization: string;
							groups: string[];
							is_sponsor: boolean;
						}
					>;
					groups: string[];
					allowed_groups: string[];
				}>(getBackendURL(`/esr/permitted_recipients/${activeId}`)).then(
					({ data }) => {
						const options: RecipientOption[] = Object.entries(data["users"])
							.map(
								([email, u]) =>
									[email, u["first_name"], u["last_name"], u["organization"], u.groups, u.is_sponsor] as [
										string,
										string,
										string,
										string,
										string[],
										boolean,
									],
							)
							.filter(([email]) => email !== user?.email);
						setRecipientOptions(options);
						// setGroupOptions([...data["groups"]]);
						setAllowedGroups([...data["allowed_groups"]]);
					},
				);
			}
		}, [user?.email, activeId]);

	const startEditing = useCallback(() => {
		setEditing(true);
	}, []);

	const finishEditing = useCallback(() => {
		setEditing(false);
	}, []);

	const addRecipients = useCallback(() => {
		dispatch(
			updateThread(esrId, thread.id, {
				recipients: [...thread.receiver_ids, ...recipients],
			}),
		);
		setRecipients([]); // Clear the input
		finishEditing();
	}, [esrId, thread, recipients]);

	return (
		<>
			<Chip
				icon={<AddOutlined />}
				label="Add"
				color="primary"
				variant="outlined"
				onClick={startEditing}
			/>
			<Dialog onClose={finishEditing} open={editing} fullWidth maxWidth="sm">
				<DialogTitle>Add to Conversation</DialogTitle>
				<DialogContent sx={{ overflow: "visible" }}>
					<Stack spacing={3} sx={{ mt: 2, mb: 10 }}>
						{/* User Selection */}
						<Stack direction="row" alignItems="flex-start" spacing={2}>
							<Typography
								fontWeight={500}
								fontSize={13}
								color="text.secondary"
								sx={{ width: 60, pt: 1 }}
							>
								Users:
							</Typography>
							<Stack spacing={2} sx={{ width: 320 }}>
								<FormControl size="small" fullWidth>
									<Autocomplete
										disableCloseOnSelect
										multiple
										options={allowed_user_groups}
										renderInput={(params) => (
											<TextField
												{...params}
												label="User Group Filter"
												placeholder="Choose filter"
											/>
										)}
										onChange={(e, newValue) => {
											setSelectedUserGroups(
												newValue && newValue.length ? newValue : [],
											);
											if (newValue.length === 0) setRecipients([]);
										}}
										getOptionLabel={(option) => option}
										renderOption={(props, option, { selected }) => {
											const { key, ...liProps } = props;
											return (
												<li {...liProps} key={key}>
													<Checkbox
														icon={icon}
														checkedIcon={checkedIcon}
														sx={{ mr: 1 }}
														checked={selected}
													/>
													{option}
												</li>
											);
										}}
									/>
								</FormControl>

								{selected_user_groups.length > 0 && (
									<FormControl size="small" fullWidth>
										<Autocomplete
											disableCloseOnSelect
											multiple
											options={filteredRecipientOptions}
											groupBy={(option) => option[0]}
											renderInput={(params) => (
												<TextField
													{...params}
													label="Selected Users"
													placeholder="Choose Users"
												/>
											)}
											onChange={(e, newValue) => {
												let user_emails = newValue.map((val) => val[1]);
												setRecipients(user_emails);
											}}
											getOptionLabel={(option) => `${option[2]} <${option[1]}>`}
											renderOption={(props, option, { selected }) => {
												const { key, ...liProps } = props;
												return (
													<li {...liProps} key={`${option[0]}-${option[1]}`}>
													<Checkbox
														icon={icon}
														checkedIcon={checkedIcon}
														sx={{ mr: 1 }}
														checked={selected}
													/>
													{`${option[2]} <${option[1]}>`}
												</li>
												);
											}}
											filterOptions={(options, { inputValue }) =>
												options.filter(
													([group, email, firstName, lastName]) =>
														email
															.toLowerCase()
															.includes(inputValue.toLowerCase()) ||
														firstName
															.toLowerCase()
															.includes(inputValue.toLowerCase()) ||
														lastName
															.toLowerCase()
															.includes(inputValue.toLowerCase()),
												)
											}
											isOptionEqualToValue={(option, value) =>
												option[1] === value[1]
											}
										/>
									</FormControl>
								)}
							</Stack>
						</Stack>
					</Stack>
					<Box sx={{ mt: "10px" }}>
						<Button
							variant="contained"
							sx={{ float: "right" }}
							onClick={addRecipients}
						>
							Add
						</Button>
					</Box>
				</DialogContent>
			</Dialog>
		</>
	);
}
